import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ArrowRight, Mic, Headphones, Upload, Clock, Users, Award } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';

const PodcastEditingService = () => {
  const { currentLanguage } = useLanguage();
  const { city } = useParams<{ city?: string }>();
  const { toast } = useToast();
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const title = {
      de: `Podcast Editing & Launch ${city ? `in ${city}` : 'Schweiz'} | Future Level`,
      en: `Podcast Editing & Launch ${city ? `in ${city}` : 'Switzerland'} | Future Level`,
      fr: `Montage & Lancement Podcast ${city ? `à ${city}` : 'Suisse'} | Future Level`,
      it: `Editing & Lancio Podcast ${city ? `a ${city}` : 'Svizzera'} | Future Level`
    };

    const description = {
      de: `Professionelles Podcast Editing für Schweizer Unternehmen. Audio-Cleanup, Intro/Outro, Syndication. Thought Leadership für B2B-CEOs und Berater.`,
      en: `Professional podcast editing for Swiss businesses. Audio cleanup, intro/outro, syndication. Thought leadership for B2B CEOs and consultants.`,
      fr: `Montage podcast professionnel pour entreprises suisses. Nettoyage audio, intro/outro, syndication. Leadership éclairé pour PDG B2B et consultants.`,
      it: `Editing podcast professionale per aziende svizzere. Pulizia audio, intro/outro, sindacazione. Thought leadership per CEO B2B e consulenti.`
    };

    updateMetaTags({
      title: title[currentLanguage],
      description: description[currentLanguage],
      canonicalUrl: `https://futurelevel.ch/${currentLanguage}/podcast-editing${city ? `/${city}` : ''}/`,
      language: currentLanguage
    });
  }, [currentLanguage, city]);

  const services = [
    {
      icon: Headphones,
      title: {
        de: 'Audio-Cleanup & Mastering',
        en: 'Audio Cleanup & Mastering',
        fr: 'Nettoyage Audio & Mastering',
        it: 'Pulizia Audio & Mastering'
      },
      description: {
        de: 'Professionelle Rauschunterdrückung, EQ, Kompression und Audio-Mastering',
        en: 'Professional noise reduction, EQ, compression and audio mastering',
        fr: 'Réduction bruit professionnelle, égalisation, compression et mastering audio',
        it: 'Riduzione rumore professionale, EQ, compressione e mastering audio'
      },
      features: ['Noise Removal', 'EQ & Compression', 'Stereo Enhancement', 'Volume Leveling'],
      price: 'CHF 80-200/Stunde',
      deliveryTime: '24-48h'
    },
    {
      icon: Mic,
      title: {
        de: 'Intro/Outro Produktion',
        en: 'Intro/Outro Production',
        fr: 'Production Intro/Outro',
        it: 'Produzione Intro/Outro'
      },
      description: {
        de: 'Professionelle Intro- und Outro-Musik mit Sprecher und Branding-Elementen',
        en: 'Professional intro and outro music with voice-over and branding elements',
        fr: 'Musique intro et outro professionnelle avec voix off et éléments branding',
        it: 'Musica intro e outro professionale con voice-over ed elementi di branding'
      },
      features: ['Custom Music', 'Voice-Over', 'Brand Integration', 'Multiple Versions'],
      price: 'CHF 400-1,200',
      deliveryTime: '3-5 Tage'
    },
    {
      icon: Upload,
      title: {
        de: 'Podcast Launch & Syndication',
        en: 'Podcast Launch & Syndication',
        fr: 'Lancement & Syndication Podcast',
        it: 'Lancio & Sindacazione Podcast'
      },
      description: {
        de: 'Kompletter Podcast-Launch mit Verteilung auf alle wichtigen Plattformen',
        en: 'Complete podcast launch with distribution to all major platforms',
        fr: 'Lancement podcast complet avec distribution sur toutes plateformes majeures',
        it: 'Lancio podcast completo con distribuzione su tutte le piattaforme principali'
      },
      features: ['Platform Setup', 'RSS Feed', 'SEO Optimization', 'Analytics Setup'],
      price: 'CHF 800-2,500',
      deliveryTime: '5-7 Tage'
    }
  ];

  const thoughtLeadershipBenefits = [
    {
      title: {
        de: 'B2B-Vertrauen aufbauen',
        en: 'Build B2B Trust',
        fr: 'Construire Confiance B2B',
        it: 'Costruire Fiducia B2B'
      },
      description: {
        de: 'Podcasts positionieren Schweizer CEOs als Branchenexperten und Thought Leader',
        en: 'Podcasts position Swiss CEOs as industry experts and thought leaders',
        fr: 'Podcasts positionnent PDG suisses comme experts secteur et leaders opinion',
        it: 'I podcast posizionano i CEO svizzeri come esperti del settore e thought leader'
      },
      impact: '+450% Branchenglaubwürdigkeit'
    },
    {
      title: {
        de: 'Lead-Generierung',
        en: 'Lead Generation',
        fr: 'Génération Leads',
        it: 'Generazione Lead'
      },
      description: {
        de: 'Podcast-Hörer werden zu qualifizierten B2B-Leads und Geschäftspartnern',
        en: 'Podcast listeners become qualified B2B leads and business partners',
        fr: 'Auditeurs podcast deviennent leads B2B qualifiés et partenaires business',
        it: 'Gli ascoltatori del podcast diventano lead B2B qualificati e partner commerciali'
      },
      impact: '+280% qualifizierte Leads'
    },
    {
      title: {
        de: 'Netzwerk-Aufbau',
        en: 'Network Building',
        fr: 'Construction Réseau',
        it: 'Costruzione Network'
      },
      description: {
        de: 'Interview-Gäste werden zu wertvollen Geschäftskontakten und Kooperationen',
        en: 'Interview guests become valuable business contacts and collaborations',
        fr: 'Invités interviews deviennent contacts business précieux et collaborations',
        it: 'Gli ospiti delle interviste diventano contatti commerciali preziosi e collaborazioni'
      },
      impact: '+350% Netzwerk-Qualität'
    }
  ];

  const swissMarkets = [
    {
      sector: {
        de: 'Management Beratung',
        en: 'Management Consulting',
        fr: 'Conseil en Gestion',
        it: 'Consulenza Gestionale'
      },
      examples: ['McKinsey Alumni', 'Independent Berater', 'Boutique Agencies'],
      topics: ['Digital Transformation', 'Leadership', 'Strategy', 'Innovation'],
      avgBudget: 'CHF 2,000-8,000/Monat'
    },
    {
      sector: {
        de: 'Fintech & Banking',
        en: 'Fintech & Banking',
        fr: 'Fintech & Banque',
        it: 'Fintech & Banking'
      },
      examples: ['Crypto Valley', 'Traditional Banks', 'Payment Providers'],
      topics: ['Blockchain', 'RegTech', 'Digital Banking', 'Investment Trends'],
      avgBudget: 'CHF 3,500-15,000/Monat'
    },
    {
      sector: {
        de: 'Tech CEOs & Startups',
        en: 'Tech CEOs & Startups',
        fr: 'PDG Tech & Startups',
        it: 'CEO Tech & Startup'
      },
      examples: ['Scale-ups', 'Unicorns', 'Tech Founders'],
      topics: ['Entrepreneurship', 'Scaling', 'Tech Trends', 'Funding'],
      avgBudget: 'CHF 1,500-6,000/Monat'
    }
  ];

  const handleAutomationSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!webhookUrl) {
      toast({
        title: "Error",
        description: "Please enter your Zapier webhook URL",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors",
        body: JSON.stringify({
          service: 'podcast_editing',
          action: 'automation_setup',
          timestamp: new Date().toISOString(),
          triggered_from: window.location.origin,
          message: 'Podcast editing automation workflow triggered'
        }),
      });

      toast({
        title: "Automation Setup Triggered",
        description: "Your podcast editing automation workflow has been triggered. Check your Zapier dashboard for confirmation.",
      });
    } catch (error) {
      console.error("Error triggering webhook:", error);
      toast({
        title: "Error",
        description: "Failed to trigger the automation. Please check the URL and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <Headphones className="w-4 h-4 mr-2" />
                {currentLanguage === 'de' ? 'Podcast Editing' : 
                 currentLanguage === 'fr' ? 'Montage Podcast' :
                 currentLanguage === 'it' ? 'Editing Podcast' : 'Podcast Editing'}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentLanguage === 'de' ? 'Thought Leadership durch Podcasting' :
                 currentLanguage === 'fr' ? 'Leadership Éclairé par Podcasting' :
                 currentLanguage === 'it' ? 'Thought Leadership tramite Podcasting' :
                 'Thought Leadership Through Podcasting'}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentLanguage === 'de' ? 'Professionelle Podcast-Produktion für Schweizer CEOs, Berater und B2B-Experten. Von Audio-Cleanup bis Platform-Launch.' :
                 currentLanguage === 'fr' ? 'Production podcast professionnelle pour PDG suisses, consultants et experts B2B. Du nettoyage audio au lancement plateforme.' :
                 currentLanguage === 'it' ? 'Produzione podcast professionale per CEO svizzeri, consulenti ed esperti B2B. Dalla pulizia audio al lancio piattaforma.' :
                 'Professional podcast production for Swiss CEOs, consultants and B2B experts. From audio cleanup to platform launch.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  {currentLanguage === 'de' ? 'Podcast Demo hören' :
                   currentLanguage === 'fr' ? 'Écouter Démo Podcast' :
                   currentLanguage === 'it' ? 'Ascolta Demo Podcast' :
                   'Listen to Podcast Demo'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  {currentLanguage === 'de' ? 'Kostenlose Beratung' :
                   currentLanguage === 'fr' ? 'Consultation Gratuite' :
                   currentLanguage === 'it' ? 'Consulenza Gratuita' :
                   'Free Consultation'}
                </Button>
              </div>

              {/* Thought Leadership Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">450%</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Mehr Glaubwürdigkeit' :
                     currentLanguage === 'fr' ? 'Plus Crédibilité' :
                     currentLanguage === 'it' ? 'Più Credibilità' :
                     'More Credibility'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">280%</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Qualifizierte Leads' :
                     currentLanguage === 'fr' ? 'Leads Qualifiés' :
                     currentLanguage === 'it' ? 'Lead Qualificati' :
                     'Qualified Leads'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">24h</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Express Editing' :
                     currentLanguage === 'fr' ? 'Montage Express' :
                     currentLanguage === 'it' ? 'Editing Express' :
                     'Express Editing'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">50+</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Swiss Podcasts' :
                     currentLanguage === 'fr' ? 'Podcasts Suisses' :
                     currentLanguage === 'it' ? 'Podcast Svizzeri' :
                     'Swiss Podcasts'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Podcast-Produktions-Services' :
                 currentLanguage === 'fr' ? 'Services Production Podcast' :
                 currentLanguage === 'it' ? 'Servizi Produzione Podcast' :
                 'Podcast Production Services'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="text-center mb-4">
                        <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          {service.title[currentLanguage]}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {service.description[currentLanguage]}
                        </p>
                        
                        <div className="grid grid-cols-2 gap-1 mb-4 text-xs">
                          {service.features.map((feature, idx) => (
                            <Badge key={idx} variant="secondary" className="justify-center text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="flex justify-between items-center mb-4 text-sm">
                          <span className="text-muted-foreground">
                            {service.deliveryTime}
                          </span>
                          <span className="text-lg font-bold text-primary">
                            {service.price}
                          </span>
                        </div>
                        
                        <Button className="w-full">
                          {currentLanguage === 'de' ? 'Mehr erfahren' :
                           currentLanguage === 'fr' ? 'En savoir plus' :
                           currentLanguage === 'it' ? 'Scopri di più' :
                           'Learn More'}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Thought Leadership Benefits */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Warum Schweizer B2B-Experten auf Podcasts setzen' :
                 currentLanguage === 'fr' ? 'Pourquoi les Experts B2B Suisses Misent sur les Podcasts' :
                 currentLanguage === 'it' ? 'Perché gli Esperti B2B Svizzeri Puntano sui Podcast' :
                 'Why Swiss B2B Experts Rely on Podcasts'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {thoughtLeadershipBenefits.map((benefit, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">
                      {benefit.title[currentLanguage]}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {benefit.description[currentLanguage]}
                    </p>
                    <div className="text-lg font-bold text-green-500">
                      {benefit.impact}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Swiss Markets */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Schweizer B2B-Märkte für Podcasting' :
                 currentLanguage === 'fr' ? 'Marchés B2B Suisses pour Podcasting' :
                 currentLanguage === 'it' ? 'Mercati B2B Svizzeri per Podcasting' :
                 'Swiss B2B Markets for Podcasting'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {swissMarkets.map((market, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">
                      {market.sector[currentLanguage]}
                    </h3>
                    
                    <div className="mb-4">
                      <div className="text-sm font-medium mb-2">
                        {currentLanguage === 'de' ? 'Zielgruppen:' :
                         currentLanguage === 'fr' ? 'Cibles:' :
                         currentLanguage === 'it' ? 'Target:' :
                         'Targets:'}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {market.examples.map((example, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-sm font-medium mb-2">
                        {currentLanguage === 'de' ? 'Beliebte Themen:' :
                         currentLanguage === 'fr' ? 'Sujets Populaires:' :
                         currentLanguage === 'it' ? 'Argomenti Popolari:' :
                         'Popular Topics:'}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {market.topics.map((topic, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="text-lg font-bold text-primary">
                      {market.avgBudget}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Automation Section */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Podcast-Automation mit Zapier' :
                 currentLanguage === 'fr' ? 'Automatisation Podcast avec Zapier' :
                 currentLanguage === 'it' ? 'Automazione Podcast con Zapier' :
                 'Podcast Automation with Zapier'}
              </h2>
              
              <Card className="p-8">
                <div className="text-center mb-6">
                  <p className="text-lg text-muted-foreground mb-4">
                    {currentLanguage === 'de' ? 'Automatisieren Sie Ihre Podcast-Workflows: Von der Aufnahme bis zur Veröffentlichung.' :
                     currentLanguage === 'fr' ? 'Automatisez vos workflows podcast: De l\'enregistrement à la publication.' :
                     currentLanguage === 'it' ? 'Automatizza i tuoi flussi podcast: Dalla registrazione alla pubblicazione.' :
                     'Automate your podcast workflows: From recording to publication.'}
                  </p>
                </div>
                
                <form onSubmit={handleAutomationSetup} className="space-y-4">
                  <div>
                    <Label htmlFor="webhook">
                      {currentLanguage === 'de' ? 'Zapier Webhook URL:' :
                       currentLanguage === 'fr' ? 'URL Webhook Zapier:' :
                       currentLanguage === 'it' ? 'URL Webhook Zapier:' :
                       'Zapier Webhook URL:'}
                    </Label>
                    <Input
                      id="webhook"
                      type="url"
                      placeholder="https://hooks.zapier.com/hooks/catch/..."
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="text-center">
                    <Button type="submit" disabled={isLoading} className="bg-gradient-to-r from-primary to-primary/90">
                      {isLoading ? 'Triggering...' : 
                       currentLanguage === 'de' ? 'Automation einrichten' :
                       currentLanguage === 'fr' ? 'Configurer Automatisation' :
                       currentLanguage === 'it' ? 'Configura Automazione' :
                       'Setup Automation'}
                    </Button>
                  </div>
                </form>
                
                <div className="mt-6 text-sm text-muted-foreground">
                  <p>
                    {currentLanguage === 'de' ? 'Erstellen Sie einen Zap in Zapier mit einem Webhook-Trigger und verbinden Sie Ihre Podcast-Tools.' :
                     currentLanguage === 'fr' ? 'Créez un Zap dans Zapier avec un déclencheur webhook et connectez vos outils podcast.' :
                     currentLanguage === 'it' ? 'Crea uno Zap in Zapier con un trigger webhook e collega i tuoi strumenti podcast.' :
                     'Create a Zap in Zapier with a webhook trigger and connect your podcast tools.'}
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              {currentLanguage === 'de' ? 'Bereit für Ihren Thought Leadership Podcast?' :
               currentLanguage === 'fr' ? 'Prêt pour votre Podcast de Leadership Éclairé?' :
               currentLanguage === 'it' ? 'Pronti per il vostro Podcast di Thought Leadership?' :
               'Ready for Your Thought Leadership Podcast?'}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {currentLanguage === 'de' ? 'Positionieren Sie sich als Branchenexperte. Wir kümmern uns um die komplette Podcast-Produktion.' :
               currentLanguage === 'fr' ? 'Positionnez-vous comme expert du secteur. Nous nous occupons de toute la production podcast.' :
               currentLanguage === 'it' ? 'Posizionatevi come esperti del settore. Ci occupiamo di tutta la produzione podcast.' :
               'Position yourself as an industry expert. We handle the complete podcast production.'}
            </p>
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
              <a href={`/${currentLanguage}/kontakt/`}>
                {currentLanguage === 'de' ? 'Podcast starten' :
                 currentLanguage === 'fr' ? 'Démarrer Podcast' :
                 currentLanguage === 'it' ? 'Inizia Podcast' :
                 'Start Podcast'}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PodcastEditingService;